"use client";

import { useTranslations } from "next-intl";
import { useState } from "react";
import { useAccount } from "wagmi";
import { WalletAddress } from "@/components/core";
import { VoteButton } from "@/components/features/dao/voting/VoteButton";
import { VoteDrawer } from "@/components/features/dao/voting/VoteDrawer";
import { VotedDnmBadge } from "@/components/features/dao/voting/VotedDnmBadge";
import { useCurrentCycle } from "@/hooks/dao/useCurrentCycle";
import { useVotingPeriod } from "@/hooks/dao/useVotingPeriod";
import { useUserVotesForCycle } from "@/hooks/dao/voting/useUserVotesForCycle";
import { Link } from "@/i18n/navigation";
import type { MergedCandidate } from "@/types/dao/election";
import { EyeChip } from "./EyeChip";
import { RankBadge } from "./RankBadge";
import { VoteCountBadge } from "./VoteCountBadge";

interface ElectionRowProps {
  candidate: MergedCandidate;
  /**
   * When provided, the row renders in "ended" mode for the post-voting
   * MemberList: leading tile becomes a RankBadge instead of the running
   * VoteCountBadge, and the vote affordances (button + voted badge) are
   * suppressed since voting has closed.
   */
  rank?: number;
  /**
   * Defaults to true. Set to false when the row is part of a historical
   * browse (past-cycle candidate view) — then the vote button + voted badge
   * + drawer are entirely suppressed instead of rendering as disabled.
   * Disabled-with-tooltip is reserved for the live current-cycle voting view.
   */
  interactive?: boolean;
  /**
   * Width of the VoteCountBadge digit area in `ch` units. Forwarded from the
   * parent list so every tile in the same list shares a width — the longest
   * formatted vote count drives the dimension.
   */
  voteTileDigitsCh?: number;
}

/**
 * Candidate row — used by both the active CandidatesList and the post-vote
 * MemberList. Single component, single source of truth for layout / RTL /
 * eye-chip Link target / WalletAddress wiring.
 *
 * Source order under dir=rtl flex-row (first source child → physical-RIGHT):
 *   [Rank or VoteCount tile] [name+wallet col flex-1] [VoteButton?] [VotedDnmBadge?] [EyeChip Link]
 * Visual physical order under RTL → [EyeChip-LEFT | … | tile-RIGHT]
 */
export const ElectionRow: React.FC<ElectionRowProps> = ({
  candidate,
  rank,
  interactive = true,
  voteTileDigitsCh,
}) => {
  const t = useTranslations("dao");
  const displayName = candidate.name ?? candidate.nickname ?? candidate.email;
  const isEnded = rank !== undefined;
  // Combined gate: vote affordances render only when the row is NOT in ranked
  // (ended) mode AND the parent says interaction is allowed. Live current-
  // cycle voting view → interactive=true (default); historical browse →
  // interactive=false.
  const showVoteAffordance = !isEnded && interactive;
  const detailHref = `/info/dao/candidate/${candidate.walletAddress.toLowerCase()}`;

  // Phase 2 hooks — injected per ui-impl.md §4.9
  const { address: userAddress, isConnected } = useAccount();
  const { cycle } = useCurrentCycle();
  const { isVotingPeriod } = useVotingPeriod(cycle);
  // Per-candidate vote tracking via backend dao_vote table — populated by
  // useVoteSubmit POSTing to /election/votes after each successful tx.
  // Backend validates the tx receipt against chain before inserting, so this
  // is as authoritative as the chain itself for "did this user vote here".
  // Election.sol has no voteOf[candidate][voter] mapping and emits no events,
  // so the per-voter-per-candidate index lives off-chain by necessity.
  const { getAmount } = useUserVotesForCycle(cycle);
  const userVotedForThisCandidate = getAmount(candidate.walletAddress);
  const hasUserVoted = userVotedForThisCandidate > 0n;
  const [drawerOpen, setDrawerOpen] = useState(false);

  const isSelf =
    userAddress !== undefined &&
    userAddress.toLowerCase() === candidate.walletAddress.toLowerCase();

  return (
    <>
      <div className="flex h-12 w-full items-center gap-2 rounded-sm bg-bg-secondary py-2 pe-2 ps-0">
        {/* Child #1 = physical-RIGHT under dir=rtl. RankBadge in ended mode
            (post-voting MemberList) shows the candidate's rank ۱-۸; otherwise
            VoteCountBadge shows the running DNM tally during voting. */}
        {isEnded ? (
          <RankBadge rank={rank} />
        ) : (
          <VoteCountBadge
            voteCountRaw={candidate.totalReceivedVoteDnmWei}
            digitsCh={voteTileDigitsCh}
          />
        )}

        {/* Child #2 = name+wallet column (flex-1, items-start aligns to physical-RIGHT under RTL) */}
        <div className="flex flex-1 min-w-0 flex-col items-start gap-1 pt-2 pb-1 text-text-primary">
          {/* Truncate to dodge cards exploding when a candidate registers a long
              nickname. max-w grows with viewport so we don't punish desktop. */}
          <span className="block truncate max-w-[140px] sm:max-w-[200px] md:max-w-[280px] lg:max-w-[360px] text-xs font-medium leading-[1.4]">
            {displayName}
          </span>
          <WalletAddress
            address={candidate.walletAddress}
            size={12}
            truncateSize="md"
            responsive
            className="w-full justify-start gap-1 space-x-0"
            addressClassName="text-text-primary text-xs font-medium leading-[1.4] mt-0"
            buttonClassName="size-5 min-w-0 px-0 text-text-primary"
          />
        </div>

        {/* Vote affordances — hidden in ended mode since voting has closed. */}
        {showVoteAffordance && (
          <>
            {/* Child #3 = vote button (Phase 2 — replaces D10 placeholder) */}
            <VoteButton
              candidateAddress={candidate.walletAddress}
              isSelf={isSelf}
              isVotingPeriod={isVotingPeriod ?? false}
              isWalletConnected={isConnected}
              hasUserVoted={hasUserVoted}
              onClick={() => setDrawerOpen(true)}
            />

            {/* Child #4 = per-candidate voted badge — only renders when user
                has voted for THIS candidate in the current cycle (amount > 0n). */}
            <VotedDnmBadge amount={userVotedForThisCandidate} />
          </>
        )}

        {/* Child #5 = EyeChip Link (physical-LEFT under dir=rtl) */}
        <Link
          href={detailHref}
          aria-label={t("election.candidates.viewDetailsAria", {
            name: displayName ?? candidate.walletAddress,
          })}
          className="flex size-8 shrink-0 flex-col items-center justify-center rounded-xs border border-stroke-light px-1 py-0.5 text-text-primary"
        >
          <EyeChip />
        </Link>
      </div>

      {/* VoteDrawer mounts as sibling — portal handles positioning. Suppressed
          in ended mode since the row never opens it. */}
      {showVoteAffordance && (
        <VoteDrawer
          open={drawerOpen}
          onOpenChange={setDrawerOpen}
          candidate={{
            address: candidate.walletAddress,
            nickname: displayName ?? "",
          }}
        />
      )}
    </>
  );
};
